import { downloadInteractionHistoryDocs } from './../../network/downloadInteractionHistoryDocs'
import { eosCreateWorkItem } from './../../network/eosCreateWorkItem'
import { getDedupe } from './../../network/getDedupe'
import { getUploadInteractionFileReq } from './../../network/uploadInteractionHistoryFileApi'

import isEqual from 'lodash/isEqual'
import { flow, Instance, types } from 'mobx-state-tree'
import { claimEntityTask } from '../../network/claimEntityTask'
import { fetchEosEntityDetails } from '../../network/fetchEosEntityDetails'
import { fetchInteractionHistoryApi } from '../../network/fetchInteractionHistoryApi'
import { fetchSearchEntityNew } from '../../network/fetchSearchEntityNew'
import { getAllLovsReq } from '../../network/getAllLovs'
import { getEntityProgramList } from '../../network/getEntityProgramList'
import { findRiskSettingValue, getGlobalEntityQueue, getLimitTableData } from '../../network/getGlobalEntityQueue'
import { getMyQueueEntityList } from '../../network/getMyQueueEntityList'
import { getOnboardingIncidents } from '../../network/getOnboardingIncidents'
import { getViewDocumentListRequest } from '../../network/getViewDocument'
import { kycDeleteDocument } from '../../network/kycDeleteDocument'
import { resolveIncident } from '../../network/resolveIncident'
import { searchConfigEntityReq } from '../../network/searchEntityConfig'
import { getUpdateEntityStatusRequest, updateEntityStatus } from '../../network/updateEntityStatus'
import {
  updateExistingEntity,
  updateExistingEntityForIntraction,
  updateExistingEntityForIntractionReq,
} from '../../network/updateExistingEntity'
import { uploadInteractionHistoryFileApi } from '../../network/uploadInteractionHistoryFileApi'
import { EmType } from '../../screens/EntityManagement'
import { generateRandomString, getDescriptionFromLOVValue, getErrorMessage, MessageTypes } from '../../utils'
import { getDocumentCategoryName, RootStore } from '../store/Root'

const LovData = types.model('LovData', {
  description: types.optional(types.string, ''),
  key: types.optional(types.string, ''),
  value: types.optional(types.string, ''),
})
const Programs = types.model('LovData', {
  programId: types.optional(types.string, ''),
  pName: types.optional(types.string, ''),
})

const EntityOnboarding = types
  .model('EntityOnboarding', {
    name: types.optional(types.string, ''),
    lovsList: types.array(LovData),
    poiData: types.array(LovData),
    poaData: types.array(LovData),
    pobData: types.array(LovData),
    programsList: types.array(Programs),
  })
  .actions((self) => ({
    createWorkItem: flow(function* (onboardingData: any, metadata: any, lastRequest: any, lastDedupeResponse: any) {
      let dedupeResponse = lastDedupeResponse
      try {
        if (!isEqual(onboardingData, lastRequest)) {
          dedupeResponse = yield getDedupe(onboardingData, metadata, undefined, RootStore.session.getTokens)
          if (dedupeResponse?.dupeFound) {
            RootStore.message.setMessage(
              `Records indicate that a similar or identical ${dedupeResponse?.numberOfRecords} merchant profile(s) already exist in the system. Kindly review the details and submit if you still wish to proceed`,
              MessageTypes.ERROR,
            )
            throw { dedupeFound: true, lastRequest: onboardingData, dedupeResponse }
          }
        }
        const response = yield eosCreateWorkItem(onboardingData, metadata, dedupeResponse, RootStore.session.getTokens)
        return response
      } catch (error) {
        if (!error.dedupeFound) {
          RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        }
        throw error
      }
    }),
    fetchAllLovs: flow(function* fetchAllLovs(
      key1: string,
      key2: string,
      key3: string,
      key4: string,
      key5: string,
      key6: string,
      key7: string,
      key8: string,
      key9: string,
      key10: string,
      key11: string,
      key12: string,
      key13: string,
      key14: string,
      key15: string,
    ) {
      let response
      const traceId = generateRandomString(20)
      try {
        response = yield getAllLovsReq(
          key1,
          key2,
          key3,
          key4,
          key5,
          key6,
          key7,
          key8,
          key9,
          key10,
          key11,
          key12,
          key13,
          key14,
          key15,
          RootStore.session.getTokens,
          traceId,
        )
        if (response instanceof Object) {
          return response?.findLov
        } else {
          RootStore.message.setMessage(response, MessageTypes.ERROR)
          throw { message: response }
        }
      } catch (error) {
        console.log('error', error)
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    loadInitials: flow(function* loadInitials(fetchUploadId: boolean = false) {
      try {
        const promises = new Array(6).fill(undefined)
        if (self?.lovsList?.length === 0) {
          promises[0] = (self as any).fetchAllLovs(...LOV_KEYS.slice(0, 15))
          promises[1] = (self as any).fetchAllLovs(...LOV_KEYS.slice(15))
        }
        if (RootStore?.onboarding?.businessCategoryMccCodeList?.length === 0) {
          promises[2] = RootStore.fetchAllBusinessLines()
        }
        if (self?.programsList?.length === 0) {
          promises[3] = (self as any).fetchEntityProgramList({ pageNo: 1, size: 1000 })
        }
        if (fetchUploadId) {
          promises[4] = RootStore.kycFetchUploadId()
        }
        if (!RootStore?.documentCategories || RootStore?.documentCategories?.length === 0) {
          promises[5] = RootStore.fetchKycDocCategories({ kycId: 'ALL' })
        }
        const response = yield Promise.all(promises)
        if (self?.poiData?.length === 0) {
          self.poiData = yield RootStore.fetchKycDocCategories({ documentCategory: 'POI' })
        }
        if (self?.poaData?.length === 0) {
          self.poaData = yield RootStore.fetchKycDocCategories({ documentCategory: 'POA' })
        }
        if (self?.pobData?.length === 0) {
          self.pobData = yield RootStore.fetchKycDocCategories({ documentCategory: 'POB' })
        }
        if (self?.lovsList?.length === 0) {
          self.lovsList = [...response[0], ...response[1]] as any
        }
        if (self?.programsList?.length === 0) {
          self.programsList = response[3] as any
        }

        return {
          lovData: self?.lovsList,
          poiData: self?.poiData,
          poaData: self?.poaData,
          pobData: self?.pobData,
          programList: self.programsList,
          uploadId: response[4]?.uploadId,
        }
      } catch (error) {
        console.log('error', error)
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    //getEntityProgramList
    fetchEntityProgramList: flow(function* fetchEntityProgramList(reqBody: any) {
      try {
        const response = yield getEntityProgramList(reqBody)
        return response
      } catch (error) {
        console.log('error', error)
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    fetchMyQueueList: flow(function* fetchMyQueueList(reqBody) {
      try {
        const ddListData = yield (self as any).loadInitials()
        const response = yield getMyQueueEntityList(reqBody, ddListData)
        RootStore.setPagination(response?.pagination)
        return response?.data
      } catch (error) {
        console.log('error', error)
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    fetchGlobalEntityQueue: flow(function* fetchGlobalEntityQueue(reqBody: any) {
      try {
        const ddListData = yield (self as any).loadInitials()
        const response = yield getGlobalEntityQueue(reqBody, ddListData)
        RootStore.setPagination(response?.pagination)
        return response?.data
      } catch (error) {
        console.log('error', error)
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    claimEntityTaskAsync: flow(function* claimEntityTaskAsync(taskInstanceId: string) {
      try {
        const response = yield claimEntityTask(taskInstanceId)
        RootStore.message.setMessage('Task Claimed sucessfully.', MessageTypes.SUCCESS)
        return response
      } catch (error) {
        console.log('error', error)
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    deleteKycDoc: flow(function* deleteKycDoc(uploadId: string, documentId: string) {
      try {
        const response = yield kycDeleteDocument(uploadId, documentId, 'ENTITY', RootStore.session.getTokens)
        return response
      } catch (error) {
        RootStore.message.setMessage(error?.message, MessageTypes.ERROR)
        throw error
      }
    }),
    updateEntityStatusAsync: flow(function* (
      entityData: any,
      lastRequest: any,
      lastDedupeResponse: any,
      isDedupeRequired = false,
    ) {
      let dedupeResponse = lastDedupeResponse
      try {
        const metadata = yield (self as any).loadInitials()
        if (!metadata.uploadId) {
          metadata.uploadId = entityData?.editedValues?.documentUploadId
        }
        if (isDedupeRequired && !isEqual(entityData?.editedValues, lastRequest)) {
          dedupeResponse = yield getDedupe(
            entityData?.editedValues,
            metadata,
            entityData?.editedValues?.originalMerchant,
            RootStore.session.getTokens,
          )
          if (dedupeResponse?.dupeFound) {
            RootStore.message.setMessage(
              `Records indicate that a similar or identical ${dedupeResponse?.numberOfRecords} merchant profile(s) already exist in the system. Kindly review the details and submit if you still wish to proceed`,
              MessageTypes.ERROR,
            )
            throw { dedupeFound: true, lastRequest: entityData?.editedValues, dedupeResponse }
          }
        }
        const request = getUpdateEntityStatusRequest(entityData, metadata, dedupeResponse)
        const response = yield updateEntityStatus(request, entityData?.editedValues?.taskInstanceId)
        return response
      } catch (error) {
        if (!error.dedupeFound) {
          RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        }
        throw error
      }
    }),
    updateTerminalsState: flow(function* (request: any, taskInstanceId: any) {
      try {
        const response = yield updateEntityStatus(request, taskInstanceId)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    fetchInteractionHistoryList: flow(function* fetchInteractionHistoryList(merchantId: string, abortController?: any) {
      try {
        const interactionHistoryReq = {
          merchantId,
          pageNumber: RootStore.pagination.number - 1,
          pageSize: RootStore.pagination.size || 10,
        }
        const ddData = yield (self as any).loadInitials()
        const response = yield fetchInteractionHistoryApi(interactionHistoryReq, ddData?.lovData, abortController)
        RootStore.setPagination(response?.pagination)
        return response?.data
      } catch (error) {
        console.log('error', error)
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    downloadInteractionHistoryDocsAsync: flow(function* downloadInteractionHistoryDocsAsync(
      reqBody: any,
      abortController?: any,
    ) {
      try {
        const resp = yield downloadInteractionHistoryDocs(reqBody, abortController)
        console.log('htag response', resp)
        let jsonResp
        try {
          console.log('resp', resp)
          if (resp?.data?.byteLength <= 100) {
            jsonResp = JSON.parse(new TextDecoder('utf-8').decode(resp?.data))
          }
        } catch (err) {
          console.log(err)
          throw err
        }
        if ((jsonResp && jsonResp?.status === 'PENDING') || resp?.data?.status === 'PENDING') {
          throw { message: 'Please try after sometime.' }
        } else if (jsonResp) {
          throw { message: 'Please try after sometime.' }
        }
        if (window && resp && resp?.data) {
          let mimeType: BlobPropertyBag = resp.headers['Content-Type']
          const blob = new Blob([resp?.data], {
            type: 'application/zip',
          })
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          document.body.appendChild(a)
          a.href = url
          a.download = reqBody?.requestId + '_interaction_file.zip'
          a.click()
          window.URL.revokeObjectURL(url)
          document.body.removeChild(a)
        }
        return resp
      } catch (error) {
        console.log('error', error)
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        //  throw error
      }
    }),
    uploadInteractionHistoryFileAsync: flow(function* uploadInteractionHistoryFileAsync(
      file: any,
      mid: any,
      requestId: any,
      name: any,
      merchantDocumentType: any,
      abortController?: any,
    ) {
      try {
        const reqBody = getUploadInteractionFileReq(file, mid, requestId, name, merchantDocumentType)
        const resp = yield uploadInteractionHistoryFileApi(reqBody, abortController)
        console.log('htag response', resp)
        return resp
      } catch (error) {
        console.log('error', error)
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    fetchDedupe: flow(function* (onboardingData: any, metadata: any) {
      try {
        const response = yield getDedupe(onboardingData, metadata, RootStore.session.getTokens)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    searchEntityAsyncNew: flow(function* searchEntityAsyncNew(searchStr: string, searchBy: string, pageNo) {
      try {
        const pageNumber = pageNo ?? RootStore.pagination.number
        const size = 10
        const ddListData = yield (self as any).loadInitials()
        const response = yield fetchSearchEntityNew(searchStr, searchBy, pageNumber, size, ddListData)
        RootStore.setPagination(response?.pagination)
        return response?.data
      } catch (error) {
        RootStore.message.setMessage(error?.message, MessageTypes.ERROR)
        throw error
      }
    }),
    getSearchEntityInitData: flow(function* (entityData) {
      try {
        let documents
        const ddData = yield (self as any).loadInitials()
        try {
          documents = yield getViewDocumentListRequest(
            entityData?.applicationId,
            RootStore.session.getTokens,
            EmType.Search,
          )
        } catch (er) {
          RootStore.message.setMessage(er?.message, MessageTypes.ERROR)
        }
        console.log('documents=', documents)

        const {
          searchEntityConfig: { entityConfig },
        } = yield searchConfigEntityReq(entityData?.applicationId, RootStore.session.getTokens)
        console.log('searchEntityConfig=', entityConfig)
        const formattedDocData = formatDocData(documents?.documents ?? [])
        const formattedRiskData = getFormattedRiskConfigData(entityConfig, ddData)
        return { ...entityData, ...formattedDocData, ...formattedRiskData }
      } catch (error) {
        RootStore.message.setMessage(error?.message, MessageTypes.ERROR)
        return entityData
      }
    }),
    fetchEosEntityDetailsAsync: flow(function* (entityId, userType: any) {
      try {
        const ddListData = yield (self as any).loadInitials(
          ['MAKER_SEARCH_ENTITY', 'RISK_MAKER_SEARCH_ENTITY'].includes(userType) ? true : false,
        )
        const response = yield fetchEosEntityDetails(entityId, ddListData, userType)
        if (response?.mccCodeComponent?.mccCode) {
          const mccDetails = yield RootStore.fetchMccProgramDetails(response?.mccCodeComponent?.mccCode)
          response.mccCodeComponent = {
            businessLine: {
              key: mccDetails?.businessLine,
              value: mccDetails?.businessLine,
            },
            businessCategory: mccDetails?.businessDetails?.[0]?.businessCategory,
            mccCode: mccDetails?.businessDetails?.[0]?.mccCode,
          }
        }

        return { ...response, documentUploadId: ddListData?.uploadId, requestType: 'UPDATE_PROFILE' }
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    updateExistingEntityForInteractionAsync: flow(function* (entityID, additionalReq, reqId) {
      try {
        const entityDetails = yield (self as any)?.fetchEosEntityDetailsAsync(
          entityID,
          'RISK_MAKER_SEARCH_ENTITY_NON_EDITABLE',
        )
        const ddListData = yield (self as any).loadInitials()
        const reqBody = updateExistingEntityForIntractionReq(entityDetails, additionalReq, ddListData, reqId)
        const response = yield updateExistingEntityForIntraction(reqBody)
        RootStore.message.setMessage('Entity edited sucessfully.', MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    updateExistingEntityAsync: flow(function* (entityData, originalData, lastRequest: any, lastDedupeResponse: any) {
      try {
        let dedupeResponse = lastDedupeResponse
        const ddListData = yield (self as any).loadInitials()
        if (!isEqual(entityData?.editedValues, lastRequest)) {
          dedupeResponse = yield getDedupe(
            entityData?.editedValues,
            ddListData,
            originalData,
            RootStore.session.getTokens,
          )
          if (dedupeResponse?.dupeFound) {
            throw {
              dedupeFound: true,
              lastRequest: entityData?.editedValues,
              dedupeResponse,
              message: `Records indicate that a similar or identical ${dedupeResponse?.numberOfRecords} merchant profile(s) already exist in the system. Kindly review the details and submit if you still wish to proceed`,
            }
          }
        }
        if (!ddListData.uploadId) {
          ddListData.uploadId = entityData?.editedValues?.documentUploadId
        }
        const response = yield updateExistingEntity(entityData, originalData, ddListData, dedupeResponse)
        RootStore.message.setMessage('Entity edited sucessfully.', MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    getGroupData: flow(function* (groupId, subGroupId) {
      const groupData = yield RootStore.getGroupsList(groupId)
      const subGroupData = yield RootStore.getSubGroupsList(groupId, subGroupId)
      return { groupName: groupData[0].brandName, subGroupName: subGroupData[0].brandName }
    }),
    getIncidentsAsync: flow(function* () {
      try {
        const pageNumber = RootStore.pagination.number - 1
        const size = 10
        const ddListData = yield (self as any).loadInitials()
        const response = yield getOnboardingIncidents(ddListData, pageNumber, size)
        RootStore.setPagination(response?.pagination)
        return response?.processInstances
      } catch (error) {
        console.log('error', error)
        RootStore.resetPagination()
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    resolveIncidentAsync: flow(function* (requestBody: any) {
      try {
        const response = yield resolveIncident(requestBody)
        RootStore.message.setMessage('Task Resolved sucessfully.', MessageTypes.SUCCESS)
        return response
      } catch (error) {
        console.log('error', error)
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
  }))

const entityOnboardingData = EntityOnboarding.create({
  name: '',
})

export type EntityOnboardingType = Instance<typeof EntityOnboarding>
export { EntityOnboarding, entityOnboardingData }

const LOV_KEYS = [
  'OwnerType',
  'OwnershipType',
  'BusinessChannel',
  'Gender',
  'State',
  'YearsInBusiness',
  'MerchantCategory',
  'GSTDealerCategory',
  'BusinessType',
  'PremiseType',
  'SettlementFrequencyESS',
  'DailyTransactionValue',
  'DailyTransactionVolume',
  //'ReturnPolicy',
  'DeliveryMode',
  'DocumentCategory',
  'MDRPackageId',
  'StoreType',
  'DeferredDeliveryOptions',
  'ReasonCode',
  'Status',
  'SetupCompleteInd',
]

const formatDocData = (documents: any[]) => {
  const docList = documents?.map((item) => ({
    documentCategory: item?.documentCategory ?? 'NA',
    MERCHANT_DOCUMENT_TYPE: item?.documentCategory ?? '',
    documentId: getDocumentCategoryName(item?.documentId, RootStore.documentCategories) ?? 'NA',
    documentNumber: item?.documentNumber ?? 'NA',
    dateOfExpiry: item?.dateOfExpiry ?? 'NA',
    view_: 'View_View',
    kycDocuments: item?.kycDocuments,
  }))
  return {
    authSignatoryPoi_docs_data_jdms: docList.filter(
      (item) => item?.documentCategory === 'POI' && item?.kycDocuments?.length > 0,
    ),
    authSignatoryPoA_docs_data_jdms: docList.filter(
      (item) => item?.documentCategory === 'POA' && item?.kycDocuments?.length > 0,
    ),
    businessDetailsPob_docs_data_jdms: docList.filter(
      (item) => item?.documentCategory === 'POB' && item?.kycDocuments?.length > 0,
    ),
    canceled_check_docs_data_jdms: docList.filter(
      (item) => (item?.documentCategory === 'CC' || item?.documentCategory === 'FS') && item?.kycDocuments?.length > 0,
    ),
    doc_upload_docs_data_jdms: docList.filter(
      (item) =>
        !['POI', 'POA', 'POB', 'CC', 'FS', 'RISK'].includes(item?.documentCategory) && item?.kycDocuments?.length > 0,
    ),
    risk_supporting_docs_data_jdms: docList.filter(
      (item) => item?.documentCategory === 'RISK' && item?.kycDocuments?.length > 0,
    ),
  }
}

const getFormattedRiskConfigData = (entityConfig: any, ddData: any) => {
  return {
    merchantRiskCategory: findRiskSettingValue(entityConfig, 'RISK_SETTING', 'MERCHANT_RISK'),
    allowParentIdSettlement: findRiskSettingValue(entityConfig, 'RISK_SETTING', 'ALLOW_PARENT_ID_SETTL_FLAG'),
    riskAllowTip: findRiskSettingValue(entityConfig, 'RISK_SETTING', 'TIP'),
    riskDeliveryPeriod: getDescriptionFromLOVValue(
      ddData?.lovData,
      'DeliveryMode',
      findRiskSettingValue(entityConfig, 'RISK_SETTING', 'DELIVERY_PERIOD'),
      '',
    ),
    riskDeferredDeliveryOptions: getDescriptionFromLOVValue(
      ddData?.lovData,
      'DeferredDeliveryOptions',
      findRiskSettingValue(entityConfig, 'RISK_SETTING', 'MERCHANT_DAY_DELIVERY'),
      '',
    ),
    riskInternationalAcceptance: findRiskSettingValue(entityConfig, 'RISK_SETTING', 'INTERNATIONAL_ACCEPTANCE'),
    riskCurrentTnxLimit: findRiskSettingValue(entityConfig, 'OPEN_LOOP_RISK', 'OPEN_LOOP_CURRENT_TRANSACTION_LIMIT'),
    riskCurrentExitLimit: findRiskSettingValue(entityConfig, 'OPEN_LOOP_RISK', 'OPEN_LOOP_CURRENT_EXIT_LIMIT'),
    riskCurrentTnxCount: findRiskSettingValue(entityConfig, 'OPEN_LOOP_RISK', 'OPEN_LOOP_CURRENT_TRANSACTION_COUNT'),
    limitTableData: getLimitTableData(entityConfig),
  }
}
